import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Spinner } from "../../components/Brand";

const STATUSES = ["New", "Contacted", "Closed"];
const STATUS_COLORS = {
    New: { bg: "bg-[#D4AF37]/20", text: "text-[#D4AF37]", border: "border-[#D4AF37]/40" },
    Contacted: { bg: "bg-blue-500/20", text: "text-blue-300", border: "border-blue-500/40" },
    Closed: { bg: "bg-emerald-500/20", text: "text-emerald-300", border: "border-emerald-500/40" },
};

function fmtDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
    });
}

export default function AdminEnquiries() {
    const [items, setItems] = useState([]);
    const [counters, setCounters] = useState({ all: 0, New: 0, Contacted: 0, Closed: 0 });
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [sort, setSort] = useState("created_at:desc");
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [confirmDel, setConfirmDel] = useState(null);
    const limit = 25;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                limit: String(limit),
                skip: String(page * limit),
                sort,
            });
            if (q) params.set("q", q);
            if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
            const { data } = await api.get(`/admin/enquiries?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
            setCounters(data.counters || {});
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load enquiries");
        } finally {
            setLoading(false);
        }
    }, [q, statusFilter, sort, page]);

    useEffect(() => { load(); }, [load]);

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => setPage(0), 1);
        return () => clearTimeout(t);
    }, [q, statusFilter, sort]);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/admin/enquiries/${id}/status`, { status });
            toast.success(`Marked as ${status}`);
            setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
            if (detail?.id === id) setDetail({ ...detail, status });
            // Refresh counters
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Failed to update status");
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/admin/enquiries/${id}`);
            toast.success("Enquiry deleted");
            setConfirmDel(null);
            if (detail?.id === id) setDetail(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || "Failed to delete");
        }
    };

    const exportCsv = async () => {
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
            const { data } = await api.get(`/admin/enquiries/export.csv?${params.toString()}`, {
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Failed to export CSV");
        }
    };

    const pages = Math.max(1, Math.ceil(total / limit));

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="lx-label text-[#D4AF37] mb-2">Communications</div>
                    <h1 className="font-serif text-4xl text-white">Enquiries</h1>
                    <p className="text-xs text-white/40 mt-1">
                        Website contact submissions · {total.toLocaleString()} matching
                    </p>
                </div>
                <button
                    data-testid="enq-export-csv"
                    onClick={exportCsv}
                    className="px-5 py-2.5 text-xs uppercase tracking-wider border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#180A21] transition"
                >
                    ⬇ Export CSV
                </button>
            </div>

            {/* Filter pills */}
            <div className="flex flex-wrap gap-2">
                <FilterPill
                    label={`All · ${counters.all || 0}`}
                    active={statusFilter === "all"}
                    onClick={() => setStatusFilter("all")}
                    testid="enq-filter-all"
                />
                {STATUSES.map((s) => (
                    <FilterPill
                        key={s}
                        label={`${s} · ${counters[s] || 0}`}
                        active={statusFilter === s}
                        onClick={() => setStatusFilter(s)}
                        testid={`enq-filter-${s.toLowerCase()}`}
                    />
                ))}
            </div>

            {/* Search + sort */}
            <div className="flex flex-wrap gap-3 items-center">
                <input
                    data-testid="enq-search"
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search name, email, mobile, subject…"
                    className="flex-1 min-w-[240px] bg-[#0F0614] border border-[#2B1438] px-4 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37]"
                />
                <select
                    data-testid="enq-sort"
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="bg-[#0F0614] border border-[#2B1438] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]"
                >
                    <option value="created_at:desc">Newest first</option>
                    <option value="created_at:asc">Oldest first</option>
                    <option value="name:asc">Name (A–Z)</option>
                    <option value="name:desc">Name (Z–A)</option>
                    <option value="status:asc">Status</option>
                </select>
            </div>

            {/* Table */}
            <div className="lx-card-dark overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-white/40 uppercase tracking-wider text-[10px] border-b border-[#2B1438]">
                                <th className="text-left py-3 px-4">Received</th>
                                <th className="text-left py-3 px-4">Name</th>
                                <th className="text-left py-3 px-4">Contact</th>
                                <th className="text-left py-3 px-4">Subject</th>
                                <th className="text-left py-3 px-4">Service</th>
                                <th className="text-left py-3 px-4">Status</th>
                                <th className="text-right py-3 px-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={7} className="text-center py-12">
                                    <Spinner className="mx-auto" />
                                </td></tr>
                            )}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-white/40 py-12">
                                    No enquiries match your filters.
                                </td></tr>
                            )}
                            {!loading && items.map((e) => {
                                const c = STATUS_COLORS[e.status] || STATUS_COLORS.New;
                                return (
                                    <tr
                                        key={e.id}
                                        data-testid={`enq-row-${e.id}`}
                                        className="border-t border-[#2B1438]/60 hover:bg-[#180A21]/60 transition"
                                    >
                                        <td className="py-3 px-4 text-white/60 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                                        <td className="py-3 px-4 text-white font-medium">
                                            {e.name}
                                            {e.company && <div className="text-[10px] text-white/40">{e.company}</div>}
                                        </td>
                                        <td className="py-3 px-4 text-white/70">
                                            <div className="truncate max-w-[200px]">{e.email}</div>
                                            <div className="text-[10px] text-white/40">{e.mobile}</div>
                                        </td>
                                        <td className="py-3 px-4 text-white/80 truncate max-w-[240px]">{e.subject}</td>
                                        <td className="py-3 px-4 text-white/60">{e.service || "—"}</td>
                                        <td className="py-3 px-4">
                                            <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider border ${c.bg} ${c.text} ${c.border}`}>
                                                {e.status}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <button
                                                data-testid={`enq-view-${e.id}`}
                                                onClick={() => setDetail(e)}
                                                className="text-[#D4AF37] hover:underline text-xs mr-3"
                                            >
                                                View
                                            </button>
                                            <button
                                                data-testid={`enq-delete-${e.id}`}
                                                onClick={() => setConfirmDel(e)}
                                                className="text-rose-400 hover:underline text-xs"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {pages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-[#2B1438] text-xs text-white/60">
                        <div>
                            Page {page + 1} of {pages} · {total.toLocaleString()} total
                        </div>
                        <div className="flex gap-2">
                            <button
                                data-testid="enq-prev"
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                disabled={page === 0}
                                className="px-3 py-1.5 border border-[#2B1438] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                ← Prev
                            </button>
                            <button
                                data-testid="enq-next"
                                onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
                                disabled={page >= pages - 1}
                                className="px-3 py-1.5 border border-[#2B1438] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Drawer */}
            {detail && (
                <DetailModal
                    enquiry={detail}
                    onClose={() => setDetail(null)}
                    onStatusChange={updateStatus}
                    onDelete={() => setConfirmDel(detail)}
                />
            )}

            {/* Delete confirmation */}
            {confirmDel && (
                <Confirm
                    enquiry={confirmDel}
                    onCancel={() => setConfirmDel(null)}
                    onConfirm={() => remove(confirmDel.id)}
                />
            )}
        </div>
    );
}

function FilterPill({ label, active, onClick, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-4 py-2 text-xs uppercase tracking-wider border transition ${
                active
                    ? "bg-[#D4AF37] text-[#180A21] border-[#D4AF37]"
                    : "border-[#2B1438] text-white/60 hover:border-[#D4AF37]/50 hover:text-white"
            }`}
        >
            {label}
        </button>
    );
}

function DetailModal({ enquiry, onClose, onStatusChange, onDelete }) {
    const c = STATUS_COLORS[enquiry.status] || STATUS_COLORS.New;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
            <div
                className="bg-[#180A21] border border-[#2B1438] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="enq-detail-modal"
            >
                <div className="p-6 border-b border-[#2B1438] flex justify-between items-start">
                    <div>
                        <div className="lx-label text-[#D4AF37] mb-2">Enquiry</div>
                        <h3 className="font-serif text-2xl text-white">{enquiry.subject}</h3>
                        <div className="text-xs text-white/40 mt-2">
                            Received {fmtDate(enquiry.created_at)}
                        </div>
                    </div>
                    <button
                        data-testid="enq-detail-close"
                        onClick={onClose}
                        className="text-white/40 hover:text-white text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    <Row label="Name" value={enquiry.name} />
                    <Row label="Email" value={<a href={`mailto:${enquiry.email}`} className="text-[#D4AF37] hover:underline">{enquiry.email}</a>} />
                    <Row label="Mobile" value={<a href={`tel:${enquiry.mobile}`} className="text-[#D4AF37] hover:underline">{enquiry.mobile}</a>} />
                    {enquiry.company && <Row label="Company" value={enquiry.company} />}
                    {enquiry.service && <Row label="Service" value={enquiry.service} />}
                    <div>
                        <div className="lx-label text-white/40 mb-2">Message</div>
                        <div className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap bg-[#0F0614] border border-[#2B1438] p-4">
                            {enquiry.message}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-xs text-white/40 pt-4 border-t border-[#2B1438]">
                        <div>
                            <div className="lx-label mb-1">IP Address</div>
                            <div>{enquiry.ip_address || "—"}</div>
                        </div>
                        <div>
                            <div className="lx-label mb-1">Browser</div>
                            <div>{enquiry.browser || "—"}</div>
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t border-[#2B1438] flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-white/40">Status:</span>
                        <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider border ${c.bg} ${c.text} ${c.border}`}>
                            {enquiry.status}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        {STATUSES.filter((s) => s !== enquiry.status).map((s) => (
                            <button
                                key={s}
                                data-testid={`enq-set-${s.toLowerCase()}`}
                                onClick={() => onStatusChange(enquiry.id, s)}
                                className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#2B1438] text-white/70 hover:border-[#D4AF37] hover:text-[#D4AF37] transition"
                            >
                                Mark as {s}
                            </button>
                        ))}
                        <button
                            data-testid="enq-detail-delete"
                            onClick={onDelete}
                            className="px-3 py-1.5 text-xs uppercase tracking-wider border border-rose-400/40 text-rose-300 hover:bg-rose-500/20 transition"
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Confirm({ enquiry, onCancel, onConfirm }) {
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80">
            <div className="bg-[#180A21] border border-[#2B1438] w-full max-w-md p-6" data-testid="enq-confirm-delete">
                <h3 className="font-serif text-xl text-white mb-2">Delete this enquiry?</h3>
                <p className="text-sm text-white/60 mb-6">
                    &quot;{enquiry.subject}&quot; from {enquiry.name} will be permanently removed.
                </p>
                <div className="flex justify-end gap-2">
                    <button
                        data-testid="enq-confirm-cancel"
                        onClick={onCancel}
                        className="px-4 py-2 text-xs uppercase tracking-wider border border-[#2B1438] text-white/70 hover:text-white"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="enq-confirm-yes"
                        onClick={onConfirm}
                        className="px-4 py-2 text-xs uppercase tracking-wider bg-rose-500 text-white hover:bg-rose-600"
                    >
                        Yes, delete
                    </button>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="lx-label text-white/40">{label}</div>
            <div className="col-span-2 text-white">{value}</div>
        </div>
    );
}
